import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { Analytics, getAnalytics } from "firebase/analytics";
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAuth } from "./AuthProvider";

/**
 * =============================================================================
 * ANALYTICS TRACKING GUIDE FOR ECLIPTIC
 * =============================================================================
 * 
 * WHEN TO USE WHAT:
 * 
 * 1. trackScreen(name) - Use when user SEES a new view
 *    - Main tabs: 'home', 'community', 'stats', 'map', 'profile'
 *    - Signup steps: 'signup_sport', 'signup_username', 'signup_phone', etc.
 *    - Sub-pages: 'trick_video', 'crew_profile', 'event_details'
 * 
 * 2. trackEvent(name, params) - Use when user DOES something
 *    - Button clicks: trackEvent('button_click', { button: 'upload' })
 *    - Form submits: trackEvent('form_submit', { form: 'signup' })
 *    - Toggles/changes: trackEvent('filter_change', { filter: 'skiing' })
 * 
 * 3. trackModalView(name) - Shorthand for modal/overlay views
 *    - Opens modal: trackModalView('comments')
 *    - This logs BOTH a screen view AND an event (best of both worlds)
 * 
 * 4. trackButtonClick(name, screen) - Shorthand for button taps
 *    - Important CTAs only, not every button
 * 
 * HOW TO VIEW IN GOOGLE ANALYTICS:
 * - Screens: Reports → Engagement → Screens and pages
 * - Events: Reports → Engagement → Events
 * - Event params: Click event name → see parameter breakdown
 * - Custom params: Admin → Custom definitions → Create custom dimension
 * 
 * SIGNUP FUNNEL (8 views, 1 route):
 * Track each step as a screen: signup_welcome, signup_sport, signup_skill,
 * signup_location_permission, signup_location_search, signup_phone,
 * signup_username, signup_complete
 * =============================================================================
 */

/**
 * Pre-defined screen names for consistency
 * Use these constants instead of strings to avoid typos
 */
export const Screens = {
    // Main tabs
    HOME: 'home',
    COMMUNITY: 'community',
    STATS: 'stats',
    MAP: 'map',
    UPLOAD: 'upload',

    // Profile
    PROFILE: 'profile',
    PROFILE_OTHER: 'profile_other',

    SIGNUP_WELCOME: 'signup_welcome',
    SIGNUP_BIRTHDAY: 'signup_birthday',
    SIGNUP_NAME: 'signup_name',
    SIGNUP_USERNAME: 'signup_username',
    SIGNUP_SPORTS: 'signup_sports',
    SIGNUP_SPORT_SKILL_TERRAIN: 'signup_sport_skill_terrain',
    SIGNUP_SPORT_SKILL: 'signup_sport_skill',
    SIGNUP_SPORT_TERRAIN: 'signup_sport_terrain',
    SIGNUP_LOCATION_PERMISSION: 'signup_location_permission',
    SIGNUP_LOCATION_SEARCH: 'signup_location_search',
    SIGNUP_PHONE: 'signup_phone',
    SIGNUP_PHONE_INPUT: 'signup_phone_input',
    SIGNUP_PHONE_VERIFY: 'signup_phone_verify',
    SIGNUP_AUTH: 'signup_auth',
    SIGNUP_EMAIL_PASSWORD: 'signup_email_password',
    SIGNUP_COMPLETED: 'signup_completed',

    // Content pages
    TRICK_VIDEO: 'trick_video',
    CREW_PROFILE: 'crew_profile',
    EVENT_DETAILS: 'event_details',
    BRAND_EVENT_INFO: 'brand_event_info',
    SEARCH: 'search',
    FILTER: 'filter',
    SETTINGS: 'settings',
    DELETE_ACCOUNT: 'delete_account',
    SHOP: 'shop',
    PURCHASE_SUCCESS: 'purchase_success_page',
    WELCOME: 'welcome',
    UPLOAD_SUCCESS: 'upload_success',

    // Crew pages
    CREW_UPLOAD: 'crew_upload',

    // Static / web-only pages
    CAREERS: 'careers',
    INVEST: 'invest',
    LEGAL: 'legal',
    LANDING: 'landing',

    // Login flow
    LOGIN_AUTH: 'login_auth',
    LOGIN_USERNAME: 'login_username',
    LOGIN_PHONE_INPUT: 'login_phone_input',
    LOGIN_PHONE_VERIFY: 'login_phone_verify',
    LOGIN_FORGOT_PASSWORD: 'login_forgot_password',
    LOGIN_RESET_PASSWORD: 'login_reset_password',
} as const;

/**
 * Pre-defined event names for consistency
 */
export const Events = {
    // User actions
    BUTTON_CLICK: 'button_click',
    FORM_SUBMIT: 'form_submit',
    MODAL_VIEW: 'modal_view',
    MODAL_CLOSE: 'modal_close',

    // Content interactions
    VIDEO_PLAY: 'video_play',
    VIDEO_COMPLETE: 'video_complete',
    LIKE: 'like',
    UNLIKE: 'unlike',
    COMMENT: 'comment',
    COMMENT_SUBMITTED: 'comment_submitted',
    COMMENT_DELETED: 'comment_deleted',
    SHARE: 'share',
    SAVE: 'save',

    // Signup specific
    SIGNUP_STEP_VIEW: 'signup_step_view',
    SIGNUP_STEP_COMPLETE: 'signup_step_complete',
    SIGNUP_STEP_SKIPPED: 'signup_step_skipped',
    SIGNUP_STEP_BACK: 'signup_step_back',
    SIGNUP_COMPLETE: 'signup_complete',

    // Permission events
    LOCATION_PERMISSION_RESULT: 'location_permission_result',
    PERMISSION_PROMPT_SHOWN: 'permission_prompt_shown',
    PERMISSION_RESULT: 'permission_result',

    // Login specific
    LOGIN_STEP_VIEW: 'login_step_view',
    LOGIN_STEP_BACK: 'login_step_back',
    LOGIN_ATTEMPT: 'login_attempt',
    LOGIN_SUCCESS: 'login_success',
    LOGIN_ERROR: 'login_error',

    // Auth provider events (used by both login and signup)
    AUTH_PROVIDER_SELECTED: 'auth_provider_selected',
    AUTH_PROVIDER_SUCCESS: 'auth_provider_success',
    AUTH_PROVIDER_ERROR: 'auth_provider_error',

    // Filters/toggles
    FILTER_CHANGE: 'filter_change',
    TAB_CHANGE: 'tab_change',
    SEGMENT_CHANGE: 'segment_change',

    // Paywall / monetization
    PAYWALL_PRESENTED: 'paywall_presented',
    PAYWALL_CTA_CLICKED: 'paywall_cta_clicked',
    PAYWALL_DISMISSED: 'paywall_dismissed',
    // GA4 standard ecommerce names — unlock native GA revenue reports
    BEGIN_CHECKOUT: 'begin_checkout',
    PURCHASE: 'purchase',

    // Upload funnel
    UPLOAD_ABANDONED: 'upload_abandoned',

    // Social standardization
    FOLLOW_ACTION: 'follow_action',
    BLOCK_ACTION: 'block_action',
    REPORT_ACTION: 'report_action',

    // Search / discovery
    SEARCH_QUERY_SUBMITTED: 'search_query_submitted',
    FILTER_SUBMITTED: 'filter_submitted',

    // Push notifications
    PUSH_RECEIVED: 'push_received',
    PUSH_OPENED: 'push_opened',
    PUSH_DESTINATION_REACHED: 'push_destination_reached',
    NOTIFICATION_PERMISSION_REQUESTED: 'notification_permission_requested',

    // Deep links / referrals
    DEEP_LINK_OPENED: 'deep_link_opened',
    REFERRAL_SIGNUP_ATTRIBUTED: 'referral_signup_attributed',

    // Session lifecycle
    RESUME_FROM_BACKGROUND: 'resume_from_background',

    // Account lifecycle
    DELETE_ACCOUNT_INITIATED: 'delete_account_initiated',

    // Errors
    ERROR: 'error',
    NETWORK_ERROR: 'network_error',
} as const;

/**
 * Pre-defined parameter names for custom dimensions
 * Register these in GA4: Admin → Custom definitions → Create custom dimension
 * 
 * HOW TO REGISTER IN GA4:
 * 1. Go to Admin → Custom definitions
 * 2. Click "Create custom dimension"
 * 3. Dimension name: use the value below (e.g., "button_name")
 * 4. Scope: Event
 * 5. Event parameter: same as dimension name
 */
export const Params = {
    // Screen/location context
    SCREEN_NAME: 'screen_name',
    PREVIOUS_SCREEN: 'previous_screen',

    // UI element identifiers
    BUTTON_NAME: 'button_name',
    MODAL_NAME: 'modal_name',
    TAB_NAME: 'tab_name',
    SEGMENT_NAME: 'segment_name',

    // Signup funnel
    STEP_NAME: 'step_name',
    STEP_NUMBER: 'step_number',

    // Content identifiers
    VIDEO_ID: 'video_id',
    TRICK_ID: 'trick_id',
    USER_ID: 'user_id',
    CREW_ID: 'crew_id',
    EVENT_ID: 'event_id',

    // Generic action qualifier (e.g. block/unblock, confirm/cancel)
    ACTION: 'action',

    // Filters & preferences
    SPORT: 'sport',
    SPORT_TYPE: 'sport_type',
    SPORT_INDEX: 'sport_index',
    TOTAL_SPORTS: 'total_sports',
    FILTER_TYPE: 'filter_type',
    FILTER_VALUE: 'filter_value',
    TIME_RANGE: 'time_range',

    // Engagement metrics
    DURATION_SECONDS: 'duration_seconds',
    SCROLL_DEPTH: 'scroll_depth',
    ITEM_COUNT: 'item_count',

    // Error tracking
    ERROR_MESSAGE: 'error_message',
    ERROR_CODE: 'error_code',

    // Device/platform
    PLATFORM: 'platform',

    // Permission tracking
    PERMISSION_STATUS: 'permission_status',

    // Auth tracking
    AUTH_PROVIDER: 'auth_provider',
    AUTH_METHOD: 'auth_method',
    IS_LOGIN: 'is_login',

    // =========================================================================
    // Lane A additions (2026-04-18) — registered in GA4 as custom dimensions
    // via functions/analytics/register_custom_dimensions.py
    // =========================================================================

    // Paywall / monetization
    SOURCE_SCREEN: 'source_screen',
    ENTITLEMENT_ID: 'entitlement_id',
    SUBSCRIPTION_TYPE: 'subscription_type',
    CURRENCY: 'currency',                          // GA4-reserved, auto-handled
    PRICE: 'price',                                // numeric metric
    TRANSACTION_ID: 'transaction_id',              // GA4-reserved, auto-handled
    BUTTON_TEXT: 'button_text',
    DISMISSED_AT_STEP: 'dismissed_at_step',

    // Comments
    PARENT_COMMENT_ID: 'parent_comment_id',        // logged, not registered (high-cardinality)
    COMMENT_LENGTH: 'comment_length',              // numeric metric

    // Social standardization
    TARGET_USER_ID: 'target_user_id',              // logged, not registered (high-cardinality)
    FOLLOW_TYPE: 'follow_type',
    BLOCKED_USER_ID: 'blocked_user_id',            // logged, not registered
    REPORTED_USER_ID: 'reported_user_id',          // logged, not registered
    REPORT_REASON: 'report_reason',

    // Search / discovery
    QUERY_LENGTH: 'query_length',                  // numeric metric — NOT the raw query (PII)
    RESULT_COUNT: 'result_count',                  // numeric metric
    FILTERS_APPLIED: 'filters_applied',

    // Upload funnel
    UPLOAD_STAGE: 'upload_stage',

    // Push / notifications
    NOTIFICATION_TYPE: 'notification_type',
    DEEP_LINK_TARGET: 'deep_link_target',           // logged, not registered (use link_type + destination_screen for breakdowns)
    DELIVERED_IN_FOREGROUND: 'delivered_in_foreground',
    SECONDS_SINCE_RECEIVED: 'seconds_since_received', // numeric metric
    TIME_TO_DESTINATION_MS: 'time_to_destination_ms', // numeric metric
    DESTINATION_SCREEN: 'destination_screen',

    // Deep links / referrals
    LINK_TYPE: 'link_type',
    REFERRER_USERNAME: 'referrer_username',        // logged, not registered — hash before logging (PII)

    // Permissions
    PERMISSION_TYPE: 'permission_type',

    // Session lifecycle
    BACKGROUND_DURATION_SECONDS: 'background_duration_seconds', // numeric metric
    TRIGGER_TYPE: 'trigger_type',

    // Error taxonomy
    ENDPOINT: 'endpoint',                          // logged, not registered
    RETRY_COUNT: 'retry_count',                    // numeric metric
} as const;

export const SignUpParams = {
    VIEW: 'view',
    COMPLETE: 'complete',
} as const;

interface AppAnalyticsContextType {
    // Core tracking methods
    trackScreen: (screenName: string, screenClass?: string) => Promise<void>;
    trackEvent: (name: string, params?: Record<string, any>) => Promise<void>;

    // Convenience methods (use these for common patterns)
    trackModalView: (modalName: string, extraParams?: Record<string, any>) => Promise<void>;
    trackButtonClick: (buttonName: string, screenName?: string) => Promise<void>;
    trackSignupStep: (screenName: string, stepName: string, extraParams?: Record<string, any>) => Promise<void>;

    // User properties
    setUserProperty: (key: string, value: string) => Promise<void>;

    // Admin
    setSessionTimeout: (duration: number) => Promise<void>;
    enableAnalytics: (enabled: boolean) => Promise<void>;
    isAnalyticsEnabled: () => Promise<boolean>;
    resetAnalytics: () => Promise<void>;
}

const AppAnalyticsContext = createContext<AppAnalyticsContextType | undefined>(undefined);

export const AppAnalyticsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [analytics, setAnalytics] = useState<Analytics | undefined>(undefined);
    const { firebaseApp, user } = useAuth();

    useEffect(() => {
        if (firebaseApp) {
            setAnalytics(getAnalytics(firebaseApp));
        }
    }, [firebaseApp]);

    useEffect(() => {
        if (user?.uid) {
            FirebaseAnalytics.setUserId({ userId: user.uid });
        }
    }, [user]);

    const trackScreen = async (screenName: string, screenClass?: string) => {
        try {
            console.log('[Analytics] Screen:', screenName, screenClass || '');
            await FirebaseAnalytics.setCurrentScreen({
                screenName,
                screenClassOverride: screenClass
            });
        } catch (error) {
            console.error('Screen tracking error:', error);
        }
    };

    const trackEvent = async (name: string, params?: Record<string, any>) => {
        try {
            console.log('[Analytics] Event:', name, params || {});
            await FirebaseAnalytics.logEvent({ name, params });
        } catch (error) {
            console.error('Event tracking error:', error);
        }
    };

    const setUserProperty = async (key: string, value: string) => {
        try {
            await FirebaseAnalytics.setUserProperty({ key, value });
        } catch (error) {
            console.error('User property error:', error);
        }
    };

    const setSessionTimeout = async (duration: number) => {
        try {
            await FirebaseAnalytics.setSessionTimeoutDuration({ duration });
        } catch (error) {
            console.error('Session timeout error:', error);
        }
    };

    const enableAnalytics = async (enabled: boolean) => {
        try {
            await FirebaseAnalytics.setEnabled({ enabled });
        } catch (error) {
            console.error('Enable analytics error:', error);
        }
    };

    const isAnalyticsEnabled = async () => {
        try {
            const { enabled } = await FirebaseAnalytics.isEnabled();
            return enabled;
        } catch (error) {
            console.error('Check analytics enabled error:', error);
            return false;
        }
    };

    const resetAnalytics = async () => {
        try {
            await FirebaseAnalytics.resetAnalyticsData();
        } catch (error) {
            console.error('Reset analytics error:', error);
        }
    };

    // =========================================================================
    // CONVENIENCE METHODS - Use these for common patterns
    // =========================================================================

    /**
     * Track when a modal/overlay is opened
     * Logs both a screen view AND an event so it shows in both reports
     * 
     * @example trackModalView('comments')
     * @example trackModalView('followers', { user_id: '123' })
     */
    const trackModalView = async (modalName: string, extraParams?: Record<string, any>) => {
        trackScreen(`modal_${modalName}`);
        trackEvent(Events.MODAL_VIEW, { [Params.MODAL_NAME]: modalName, ...extraParams });
    };

    /**
     * Track important button clicks
     * Only use for key CTAs (upload, signup, purchase), not every button
     * 
     * @example trackButtonClick('upload', 'home')
     * @example trackButtonClick('signup_submit', 'signup_phone')
     */
    const trackButtonClick = async (buttonName: string, screenName?: string) => {
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: buttonName,
            [Params.SCREEN_NAME]: screenName
        });
    };

    /**
     * Track signup funnel steps
     * Just call this when a step loads - GA4 will build the funnel automatically
     * 
     * @example trackSignupStep(Screens.SIGNUP_USERNAME, 'username')
     */
    const trackSignupStep = async (
        screenName: string,
        stepName: string,
        extraParams?: Record<string, any>
    ) => {
        trackScreen(screenName);
        trackEvent(Events.SIGNUP_STEP_VIEW, {
            [Params.STEP_NAME]: stepName,
            ...extraParams
        });
    };

    return (
        <AppAnalyticsContext.Provider value={{
            trackScreen,
            trackEvent,
            trackModalView,
            trackButtonClick,
            trackSignupStep,
            setUserProperty,
            setSessionTimeout,
            enableAnalytics,
            isAnalyticsEnabled,
            resetAnalytics
        }}>
            {children}
        </AppAnalyticsContext.Provider>
    );
};

export const useAnalytics = () => {
    const context = useContext(AppAnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};